import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { MaterialReactTable } from "material-react-table";
import * as M from "./styles";
import { useGetTableData2 } from "./tableHooks";
import { CirclesWithBar } from "react-loader-spinner";
import { useColumns } from "./useColumns";
import { useTableInstance } from "./useTableInstance";
import CreateRowDialog from "./CreateRowDialog";
import { useQueryClient } from "@tanstack/react-query";
import useInformationDataPlan from "../../../hooks/useInformationDataPlan";
import { useScheduling } from "./useScheduling";
import { useTableOperations } from "./useTableOperations";
import axios from "axios";
import {
  Button,
  Alert,
  Snackbar,
  Typography,
  TextField,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SearchIcon from "@mui/icons-material/Search";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import PLAN_URL from "../../../utils/PLAN_URL";
import { useDispatch, useSelector } from "react-redux";
import StopIcon from "@mui/icons-material/Stop";
import goToHelpLink from "../../../utils/goToHelpLink";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import BulkEditDialog from "./BulkEditDialog";
import { setIsScheduling, setProcessStatus } from "../../../slice/planSlice";

function Plan() {
  const [updateStatus, setUpdateStatus] = useState(false);
  const [rowSelection, setRowSelection] = useState([]);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // 'success', 'error', 'warning', 'info'
  });
  const dispatch = useDispatch();
  const { group_code, project_code } = useParams();
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const { isScheduling, processStatus } = useSelector((state) => {
    return state.plan;
  });
  const [tableDataWithExcluded, setTableDataWithExcluded] = useState([]);
  const openSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };
  const closeSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    });
  };
  const isCancelled = useRef(false);
  const queryClient = useQueryClient();
  const { data, isFetching } = useGetTableData2(
    updateStatus,
    `/api/orders/getAllOrder?group_code=${group_code}&project_code=${project_code}`
  );
  const stockInfoTestFunc = async () => {
    try {
      const res = await axios.get(
        PLAN_URL +
          `/api/item/generateItemStockInfoTest?group_code=${group_code}&project_code=${project_code}`
      );
      if (res.status === 200) {
        openSnackbar(res.data.message);
      } else {
        openSnackbar(res.data.message, "error");
      }
    } catch (e) {
      console.log(e);
      openSnackbar("오류가 발생하였습니다.", "error");
    }
  };
  useEffect(() => {
    if (!data) {
      setTableDataWithExcluded([]);
      return;
    }
    const newData = data.map((row) => ({
      ...row,
      is_excluded: row.status === 3 || row.status === 1,
    }));
    setTableDataWithExcluded(newData);
  }, [data, updateStatus]);
  // const { modelParamList } = useGetTableData2(
  //   true,
  //   `/api/rule/allRules?group_code=${group_code}&project_code=${project_code}`,
  //   "modelParamList"
  // );
  const filterdData = useMemo(() => {
    if (!searchText) return tableDataWithExcluded;
    const searchTermLower = searchText.toLowerCase();
    return tableDataWithExcluded.filter((row) => {
      return Object.values(row).some(
        (value) =>
          value !== null &&
          value !== undefined &&
          value.toString().toLowerCase().includes(searchTermLower)
      );
    });
  }, [tableDataWithExcluded, searchText]);
  const onChangeSearchValue = useCallback((event) => {
    setSearchText(event.target.value);
  }, []);
  const { infoData } = useInformationDataPlan(group_code, project_code);
  const { item } = infoData;
  const {
    doSchedulingMutation,
    handleCreateOrder: baseHandleCreateOrder,
    handleDeleteSelected: baseHandleDeleteSelected,
    handleUpdateOrder: baseHandleUpdateOrder,
  } = useTableOperations(updateStatus, openSnackbar, group_code, project_code);
  const handleCreateOrder = (params) =>
    baseHandleCreateOrder(params, setUpdateStatus);
  const handleDeleteSelected = (table) =>
    baseHandleDeleteSelected(table, setUpdateStatus);
  const handleUpdateOrder = (params) =>
    baseHandleUpdateOrder(params, setUpdateStatus);
  const { handleDoScheduling, handleCancelScheduling } = useScheduling(
    isCancelled,
    openSnackbar,
    setUpdateStatus,
    group_code,
    project_code,
    setLoading,
    setLoadingMessage,
    loadingMessage,
    dispatch
  );
  const updateRowExcludedStatus = useCallback((orderId, isExcluded) => {
    setTableDataWithExcluded((prev) =>
      prev.map((row) =>
        row.order_id === orderId ? { ...row, is_excluded: isExcluded } : row
      )
    );
  }, []);
  const columns = useColumns(item, updateRowExcludedStatus);
  // useEffect(() => {
  //   const mostRecentIndex = findMostRecentIndex(modelParamList);
  //   setSelectedModel(modelParamList[mostRecentIndex] ?? null);
  // }, [modelParamList]);
  const { table, ToolbarActions, bulkEditModalOpen, setBulkEditModalOpen } =
    useTableInstance({
      columns,
      data: filterdData || [],
      handleCreateOrder,
      handleDeleteSelected,
      handleUpdateOrder,
      CreateRowDialog,
      item,
      queryClient,
      setLoading,
      isFetching,
      pagination,
      setPagination,
      openSnackbar,
      rowSelection,
      setRowSelection,
      updateRowExcludedStatus,
    });
  const handleSchedulingClick = () => {
    const excludedOrderIds = table.options.data
      .filter(
        (row) =>
          row.is_excluded || (row.is_excluded === undefined && row.status === 3)
      )
      .map((row) => row.order_id);
    console.log("excludedOrderIds : ", excludedOrderIds);
    if (!data.length) {
      openSnackbar("주문을 추가한 후에 이용해 주세요.", "info");
      return;
    }
    if (
      excludedOrderIds.length === data.length &&
      excludedOrderIds.length &&
      data.length
    ) {
      openSnackbar("하나 이상의 주문을 선택해 주세요.", "error");
      return;
    }
    handleDoScheduling(data, excludedOrderIds, doSchedulingMutation);
  };
  useEffect(() => {
    // refresh 및 페이지 진입시 Check
    //     const statusMap = {
    //         0: '대기',
    //         1: '요청됨',
    //         2: '작업 중',
    //         3: '완료',
    //         8: '취소 요청됨',
    //         9: '취소됨',
    //         '-1': '오류'
    //       };
    async function processCheck() {
      try {
        const res = await axios.get(
          PLAN_URL +
            `/api/plan/status?group_code=${group_code}&project_code=${project_code}`
        );
        // status 0 or 3 : 이미 완료되었거나 대기.
        console.log("res.data : ", res.data);
        const { status, isDirectionTwo, isGenerateNumber, rule_id } = res.data;
        const completeStatusParams = {
          isScheduling: false,
          processStatus: 0,
        };
        // if (isDirectionTwo) {
        //   if (isGenerateNumber === 1) {
        //     console.log("isDirectionTwo && isGenerateNumber === 1");
        //     // pulling 시작. code 3이면 isSecond 넣어서 start 요청.
        //     setLoading(true);
        //     const intervalDirectionTwo = setInterval(async () => {
        //       try {
        //         const numberOneRes = await axios.get(
        //           PLAN_URL +
        //             `/api/plan/status?group_code=${group_code}&project_code=${project_code}`
        //         );
        //         setLoading(true);
        //         if (
        //           numberOneRes.data.status.code === 1 &&
        //           numberOneRes.data.isGenerateNumber === 1
        //         ) {
        //           loadingMessage !== "생산계획 수행을 요청하였습니다." &&
        //             setLoadingMessage("생산계획 수행을 요청하였습니다.");
        //         } else if (
        //           numberOneRes.data.status.code === 2 &&
        //           numberOneRes.data.isGenerateNumber === 1
        //         ) {
        //           loadingMessage !==
        //             "Backward 방향 계획 수립을 진행중입니다." &&
        //             setLoadingMessage(
        //               "Backward 방향 계획 수립을 진행중입니다."
        //             );
        //         } else if (
        //           numberOneRes.data.status.code === 3 &&
        //           numberOneRes.data.isGenerateNumber === 1
        //         ) {
        //           // start request
        //           const secondReqRes = await axios.post(
        //             PLAN_URL + "/api/plan/start",
        //             {
        //               method: "POST",
        //               headers: {
        //                 "Content-Type": "application/json",
        //               },
        //               body: {
        //                 group_code,
        //                 project_code,
        //                 model_id: rule_id,
        //                 isSecond: true,
        //               },
        //             }
        //           );
        //           if (secondReqRes.data.message === "작업이 시작되었습니다.") {
        //             setLoadingMessage(
        //               "Backward 계획 수립을 완료하고 Forward 계획 수립을 요청하였습니다."
        //             );
        //           }
        //         } else if (
        //           numberOneRes.data.status.code === 1 &&
        //           numberOneRes.data.isGenerateNumber === 2
        //         ) {
        //           loadingMessage !==
        //             "Backward 계획 수립을 완료하고 Forward 계획 수립을 요청하였습니다." &&
        //             setLoadingMessage(
        //               "Backward 계획 수립을 완료하고 Forward 계획 수립을 요청하였습니다."
        //             );
        //         } else if (
        //           numberOneRes.data.status.code === 2 &&
        //           numberOneRes.data.isGenerateNumber === 2
        //         ) {
        //           loadingMessage !== "Forward 계획 수립을 진행중입니다." &&
        //             setLoadingMessage("Forward 계획 수립을 진행중입니다.");
        //         } else if (
        //           numberOneRes.data.status.code === 3 &&
        //           numberOneRes.data.isGenerateNumber === 2
        //         ) {
        //           setLoading(false);
        //           const elapsedTime =
        //             formatTime(numberOneRes.data.finTime) ?? "0분";
        //           toast.success(`완료되었습니다. 소요시간: ${elapsedTime}`);
        //           setLoadingMessage("");
        //           const res = await axios.post(PLAN_URL + "/api/dt/generate", {
        //             method: "POST",
        //             headers: {
        //               "Content-Type": "application/json",
        //             },
        //             body: {
        //               group_code: group_code,
        //               project_code: project_code,
        //             },
        //           });
        //           openSnackbar(res.data.message);
        //           // dispatch(handleSchedulingStatus(completeStatusParams));
        //           clearInterval(intervalDirectionTwo);
        //           return;
        //         }
        //       } catch (e) {
        //         console.log(e);
        //         setLoading(false);
        //         clearInterval(intervalDirectionTwo);
        //       }
        //     }, 5000);
        //     return;
        //   } else if (isGenerateNumber === 2) {
        //     setLoading(true);
        //     console.log("isDirectionTwo && isGenerateNumber === 2"); // pulling 시작. code 3 되면 전체 종료
        //     const intervalDirectionSecond = setInterval(async () => {
        //       try {
        //         const directionTwoSecondRes = await axios.get(
        //           PLAN_URL +
        //             `/api/plan/status?group_code=${group_code}&project_code=${project_code}`
        //         );
        //         if (directionTwoSecondRes.data.status.code === 1) {
        //           loadingMessage !==
        //             "Backward 계획 수립을 완료하고 Forward 계획 수립을 요청하였습니다." &&
        //             setLoadingMessage(
        //               "Backward 계획 수립을 완료하고 Forward 계획 수립을 요청하였습니다."
        //             );
        //         } else if (directionTwoSecondRes.data.status.code === 2) {
        //           loadingMessage !== "Forward 계획 수립을 진행중입니다." &&
        //             setLoadingMessage("Forward 계획 수립을 진행중입니다.");
        //         } else if (directionTwoSecondRes.data.status.code === 3) {
        //           setLoadingMessage("");
        //           setLoading(false);
        //           const elapsedTime =
        //             formatTime(directionTwoSecondRes.data.finTime) ?? "0분";
        //           toast.success(`완료되었습니다. 소요시간: ${elapsedTime}`);
        //           const res = await axios.post(PLAN_URL + "/api/dt/generate", {
        //             method: "POST",
        //             headers: {
        //               "Content-Type": "application/json",
        //             },
        //             body: {
        //               group_code: group_code,
        //               project_code: project_code,
        //             },
        //           });
        //           openSnackbar(res.data.message);
        //           // dispatch(handleSchedulingStatus(completeStatusParams));
        //           clearInterval(intervalDirectionSecond);
        //           return;
        //         }
        //       } catch (e) {
        //         console.log(e);
        //         setLoading(false);
        //         clearInterval(intervalDirectionSecond);
        //       }
        //     }, 5000);
        //     return;
        //   }
        // }
        if (status.code === -1) {
          openSnackbar("생산계획 생성에 오류가 발생하였습니다.", "error");
          dispatch(setIsScheduling(false));
          dispatch(setProcessStatus(0));
          setLoading(false);
          setLoadingMessage("");
          return;
        }
        if (status.code === 9) {
          openSnackbar("생산계획 생성을 취소하였습니다.", "info");
          dispatch(setIsScheduling(false));
          dispatch(setProcessStatus(0));
          setLoading(false);
          setLoadingMessage("");
          return;
        }
        if (status.code === 3 || status.code === 0) {
          // 아무 이상 x
          return;
        }
        // status 9 : 취소 완료4
        // status 1 : 생산계획 요청을 보냄. 5초마다 확인
        if (status.code === 1) {
          setLoading(true);
          setLoadingMessage("생산계획 수행을 요청하였습니다.");
          dispatch(setIsScheduling(true));
          dispatch(setProcessStatus(1));
          const intervalId = setInterval(async () => {
            try {
              if (isCancelled.current) return;
              const statusRes = await axios.get(
                PLAN_URL +
                  `/api/plan/status?group_code=${group_code}&project_code=${project_code}`
              );
              const { status } = statusRes.data;
              if (status === 2) {
                setLoadingMessage("AI가 생산계획을 수립하는 중입니다.");
              }
              if (status.code === 3) {
                const res = await axios.post(PLAN_URL + "/api/dt/generate", {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                  },
                  body: { group_code: group_code, project_code: project_code },
                });
                openSnackbar(res.data.message);
                setLoading(false);
                setLoadingMessage("");
                // dispatch(handleSchedulingStatus(completeStatusParams));
                setLoading(false);
                toast.success(
                  `생산계획 생성이 완료되었습니다. 소요시간 : ${statusRes.data.progress.formatted}`
                );
                clearInterval(intervalId);
              }
            } catch (e) {
              setLoading(false);
              clearInterval(intervalId);
            }
          }, 5000);
        }
        // status 2 : 학습중에 새로고침함.
        if (status.code === 2) {
          setLoadingMessage("AI가 생산계획을 수립하는 중입니다.");
          setLoading(true);
          dispatch(setIsScheduling(true));
          dispatch(setProcessStatus(1));
          const intervalId = setInterval(async () => {
            try {
              if (isCancelled.current) return;
              const statusRes = await axios.get(
                PLAN_URL +
                  `/api/plan/status?group_code=${group_code}&project_code=${project_code}`
              );
              console.log("statusRes : ", statusRes);
              const { status } = statusRes.data;
              if (status.code === 3) {
                // const res = await axios.post(PLAN_URL + "/api/dt/generate", {
                //   method: "POST",
                //   headers: {
                //     "Content-Type": "application/json",
                //   },
                //   body: { group_code: group_code, project_code: project_code },
                // });
                // openSnackbar(res.data.message);
                setLoading(false);
                setLoadingMessage("");
                //   dispatch(handleSchedulingStatus(completeStatusParams));
                setLoading(false);
                toast.success(
                  `생산계획 생성이 완료되었습니다. 소요시간 : ${statusRes.data.progress.formatted}.`
                );
                clearInterval(intervalId);
              }
            } catch (error) {
              console.error("Status check error:", error);
              clearInterval(intervalId);
              setLoading(false);
            }
          }, 5000);
        }
        // status 8 : 취소 요청 보냄.
        if (status.code === 8) {
          setLoadingMessage("생산계획 취소를 요청하였습니다.");
          setLoading(true);
          dispatch(setIsScheduling(true));
          dispatch(setProcessStatus(1));

          const intervalId = setInterval(async () => {
            try {
              if (isCancelled.current) return;
              const statusRes = await axios.get(
                PLAN_URL +
                  `/api/plan/status?group_code=${group_code}&project_code=${project_code}`
              );
              const { status } = statusRes.data;
              console.log("useeffect cancel status : ", status);
              if (status.code === 9) {
                openSnackbar("생산계획 생성을 취소하였습니다.", "info");
                setLoading(false);
                setLoadingMessage("");
                clearInterval(intervalId);
                dispatch(setIsScheduling(false));
                dispatch(setProcessStatus(0));
                //   dispatch(handleSchedulingStatus(completeStatusParams));
                return;
              }
            } catch (error) {
              clearInterval(intervalId);
              setLoading(false);
              dispatch(setIsScheduling(false));
              dispatch(setProcessStatus(0));
              setLoadingMessage("");
            }
          }, 5000);
        }
      } catch (e) {
        console.log(e);
      }
    }
    processCheck();
  }, []);
  const schedBtnName =
    processStatus === 9
      ? "스케줄링 취소중"
      : processStatus === 1
      ? "스케줄링 취소"
      : "AI 생산계획 수립";
  const schedBtnIcon =
    processStatus === 9 || processStatus === 1 ? (
      <StopIcon />
    ) : (
      <PlayArrowIcon />
    );
  // );
  const schedBtnColor =
    processStatus === 9 || processStatus === 1 ? "error" : "primary";
  return (
    <>
      <M.Wrapper>
        <M.FrameContainer>
          <M.ToolbarContainer>
            <M.ToolbarLeft>
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ fontSize: "22px !important", color: "#111" }}
              >
                판매계획
              </Typography>
              <Tooltip
                title="이동"
                sx={{ cursor: "pointer" }}
                onClick={() =>
                  goToHelpLink(
                    "https://help.ese-tech.com/ko/planner#h-3-ai%EC%83%9D%EC%82%B0%EA%B3%84%ED%9A%8D-%ED%8E%98%EC%9D%B4%EC%A7%80"
                  )
                }
              >
                <HelpOutlineIcon />
              </Tooltip>
              <TextField
                placeholder="검색어를 입력하세요"
                size="small"
                value={searchText}
                onChange={onChangeSearchValue}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="outlined"
                color="primary"
                onClick={stockInfoTestFunc}
              >
                Stock Info 생성(임시)
              </Button>
            </M.ToolbarLeft>
            <M.ToolbarCenter>
              <Button
                variant="contained"
                color={schedBtnColor}
                startIcon={schedBtnIcon}
                sx={{
                  "&.Mui-disabled": {
                    opacity: 0.7,
                    background: "#a6a6a6",
                  },
                }}
                onClick={
                  !isScheduling ? handleSchedulingClick : handleCancelScheduling
                }
              >
                {schedBtnName}
              </Button>
            </M.ToolbarCenter>
            <M.ToolbarRight>
              <ToolbarActions table={table} />
            </M.ToolbarRight>
          </M.ToolbarContainer>
          <M.InnerContainer>
            {loading && (
              <M.DimBox>
                <M.DimContent>
                  <CirclesWithBar
                    visible={true}
                    height="100"
                    width="100"
                    color="#ffffff"
                    outerCircleColor="#ffffff"
                    innerCircleColor="#ffffff"
                    barColor="#4fa94d"
                    ariaLabel="circles-with-bar-loading"
                  />
                  <Typography variant="h6" color="white">
                    {loadingMessage}
                  </Typography>
                </M.DimContent>
              </M.DimBox>
            )}
            <MaterialReactTable
              table={table}
              muiTableBodyProps={{
                sx: { overflow: "visible", width: "100%" },
              }}
              muiTableContainerProps={{
                sx: {
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  width: "100%",
                },
              }}
              muiTablePaperProps={{
                sx: {
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  width: "100%",
                },
              }}
              renderToolbarInternalActions={() => null}
            />
          </M.InnerContainer>
        </M.FrameContainer>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={2000}
          onClose={closeSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={closeSnackbar}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
        <BulkEditDialog
          open={bulkEditModalOpen}
          onClose={() => setBulkEditModalOpen(false)}
          selectedRows={table.getSelectedRowModel().rows}
          onSave={handleUpdateOrder}
          openSnackbar={openSnackbar}
          setRowSelection={setRowSelection}
        />
      </M.Wrapper>
    </>
  );
}

export default Plan;
